// src/components/SkillToSales/NigerianMarketAnalysisDashboard.tsx
import React, { useState } from "react";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Cell,
  ResponsiveContainer,
} from "recharts";
import {
  TrendingUp,
  Users,
  DollarSign,
  Target,
  ChevronRight,
} from "lucide-react";
import { Link } from "react-router-dom";

const NigerianMarketAnalysisDashboard = () => {
  const [activeTab, setActiveTab] = useState("overview");

  // Income Segment Distribution
  const incomeSegments = [
    {
      name: "Entry Level",
      population: 40,
      income: "₦50K-150K",
      color: "#3b82f6",
    },
    {
      name: "Middle Class",
      population: 35,
      income: "₦150K-500K",
      color: "#8b5cf6",
    },
    {
      name: "Upper Middle",
      population: 20,
      income: "₦500K-1.5M",
      color: "#ec4899",
    },
    { name: "High Income", population: 5, income: "₦1.5M+", color: "#f59e0b" },
  ];

  // Market Value by Segment
  const marketValue = [
    { segment: "Entry Level", value: 360, potential: 2400, avgSpend: 150 },
    { segment: "Middle Class", value: 2250, potential: 4500, avgSpend: 500 },
    { segment: "Upper Middle", value: 5100, potential: 3400, avgSpend: 1500 },
    { segment: "High Income", value: 800, potential: 500, avgSpend: 2000 },
  ];

  // Spending Priority Breakdown
  const spendingPriorities = [
    { category: "Essentials", entry: 75, middle: 65, upper: 55 },
    { category: "Development", entry: 4, middle: 7.5, upper: 12.5 },
    { category: "Investment", entry: 7.5, middle: 12.5, upper: 17.5 },
    { category: "Lifestyle", entry: 6.5, middle: 10, upper: 12.5 },
    { category: "Savings", entry: 7, middle: 5, upper: 2.5 },
  ];

  // Purchase Intent Factors
  const purchaseFactors = [
    { factor: "ROI Proof", entry: 95, middle: 80, upper: 60 },
    { factor: "Payment Plans", entry: 90, middle: 70, upper: 30 },
    { factor: "Testimonials", entry: 85, middle: 90, upper: 70 },
    { factor: "Premium Quality", entry: 40, middle: 75, upper: 95 },
    { factor: "Speed/Convenience", entry: 50, middle: 70, upper: 90 },
    { factor: "Exclusivity", entry: 20, middle: 50, upper: 85 },
  ];

  // Opportunity Score
  const opportunityData = [
    {
      segment: "Entry Level",
      market: 8,
      competition: 8,
      difficulty: 3,
      margin: 4,
      score: 6,
    },
    {
      segment: "Middle Class",
      market: 9,
      competition: 6,
      difficulty: 5,
      margin: 7,
      score: 8,
    },
    {
      segment: "Upper Middle",
      market: 7,
      competition: 4,
      difficulty: 7,
      margin: 9,
      score: 7,
    },
    {
      segment: "High Income",
      market: 4,
      competition: 2,
      difficulty: 9,
      margin: 10,
      score: 6,
    },
  ];

  // Cultural Values Impact
  const culturalValues = [
    { value: "Family Obligation", impact: 90 },
    { value: "Education Value", impact: 95 },
    { value: "Religious Faith", impact: 85 },
    { value: "Community Status", impact: 80 },
    { value: "Entrepreneurial Spirit", impact: 88 },
    { value: "Trust Building", impact: 92 },
  ];

  // Pricing Sweet Spots
  const pricingData = [
    { segment: "Entry", min: 2.5, sweet: 25, max: 35, optimal: 25 },
    { segment: "Middle", min: 50, sweet: 100, max: 300, optimal: 150 },
    { segment: "Upper", min: 250, sweet: 500, max: 1500, optimal: 750 },
  ];

  const COLORS = ["#3b82f6", "#8b5cf6", "#ec4899", "#f59e0b"];

  return (
    <div className="w-full bg-gradient-to-br from-slate-50 to-blue-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            Nigerian Market Intelligence Dashboard
          </h1>
          <p className="text-gray-600">
            Cultural insights and purchasing power analysis for effective market
            positioning
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            "overview",
            "segments",
            "psychology",
            "cultural",
            "opportunity",
          ].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
                activeTab === tab
                  ? "bg-blue-600 text-white shadow-lg"
                  : "bg-white text-gray-600 hover:bg-gray-50"
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === "overview" && (
          <div className="space-y-6">
            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white p-6 rounded-lg shadow-lg">
                <Users className="text-blue-600 mb-2" size={32} />
                <div className="text-2xl font-bold text-gray-800">30M+</div>
                <div className="text-gray-600">Working Professionals</div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-lg">
                <DollarSign className="text-green-600 mb-2" size={32} />
                <div className="text-2xl font-bold text-gray-800">₦8.5T</div>
                <div className="text-gray-600">Total Market Value</div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-lg">
                <Target className="text-purple-600 mb-2" size={32} />
                <div className="text-2xl font-bold text-gray-800">30-60%</div>
                <div className="text-gray-600">Education Buyers</div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-lg">
                <TrendingUp className="text-orange-600 mb-2" size={32} />
                <div className="text-2xl font-bold text-gray-800">85%</div>
                <div className="text-gray-600">Online Ready</div>
              </div>
            </div>

            {/* Population & Market Value */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-lg">
                <h3 className="text-xl font-bold text-gray-800 mb-4">
                  Income Segment Distribution
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={incomeSegments}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      /* corrected label: access payload to get population */
                      label={(props: any) =>
                        props && props.payload
                          ? `${props.payload.name} ${props.payload.population}%`
                          : ""
                      }
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="population"
                    >
                      {incomeSegments.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-lg">
                <h3 className="text-xl font-bold text-gray-800 mb-4">
                  Market Value by Segment (₦ Billion)
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={marketValue}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="segment"
                      angle={-15}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="value" fill="#8b5cf6" name="Current Value" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {/* Segments Tab */}
        {activeTab === "segments" && (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h3 className="text-xl font-bold text-gray-800 mb-4">
                Spending Priority Allocation (% of Income)
              </h3>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={spendingPriorities} layout="horizontal">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="category" type="category" width={100} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="entry" fill="#3b82f6" name="Entry Level" />
                  <Bar dataKey="middle" fill="#8b5cf6" name="Middle Class" />
                  <Bar dataKey="upper" fill="#ec4899" name="Upper Middle" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h3 className="text-xl font-bold text-gray-800 mb-4">
                Optimal Pricing Range by Segment (₦ Thousands)
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={pricingData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="segment" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="min" fill="#93c5fd" name="Minimum" />
                  <Bar dataKey="sweet" fill="#3b82f6" name="Sweet Spot" />
                  <Bar dataKey="max" fill="#1e40af" name="Maximum" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Segment Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                {
                  name: "Entry Level",
                  color: "blue",
                  buyers: "30%",
                  spend: "₦150K/yr",
                  population: "12M",
                },
                {
                  name: "Middle Class",
                  color: "purple",
                  buyers: "50%",
                  spend: "₦500K/yr",
                  population: "10.5M",
                },
                {
                  name: "Upper Middle",
                  color: "pink",
                  buyers: "60%",
                  spend: "₦1.5M/yr",
                  population: "6M",
                },
              ].map((segment) => (
                <div
                  key={segment.name}
                  className="bg-white p-6 rounded-lg shadow-lg border-t-4 border-blue-600"
                >
                  <h4 className="font-bold text-lg mb-3">{segment.name}</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Population:</span>
                      <span className="font-semibold">
                        {segment.population}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Education Buyers:</span>
                      <span className="font-semibold">{segment.buyers}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Avg Annual Spend:</span>
                      <span className="font-semibold">{segment.spend}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Psychology Tab */}
        {activeTab === "psychology" && (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h3 className="text-xl font-bold text-gray-800 mb-4">
                Purchase Decision Factors by Segment (Importance Score)
              </h3>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={purchaseFactors}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="factor"
                    angle={-15}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="entry"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    name="Entry Level"
                  />
                  <Line
                    type="monotone"
                    dataKey="middle"
                    stroke="#8b5cf6"
                    strokeWidth={2}
                    name="Middle Class"
                  />
                  <Line
                    type="monotone"
                    dataKey="upper"
                    stroke="#ec4899"
                    strokeWidth={2}
                    name="Upper Middle"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-lg shadow-lg">
                <h4 className="font-bold text-lg mb-3 text-blue-900">
                  Entry Level Motivators
                </h4>
                <ul className="space-y-2 text-sm text-blue-800">
                  <li className="flex items-start">
                    <ChevronRight
                      size={16}
                      className="mr-2 mt-1 flex-shrink-0"
                    />
                    <span>Career advancement urgency</span>
                  </li>
                  <li className="flex items-start">
                    <ChevronRight
                      size={16}
                      className="mr-2 mt-1 flex-shrink-0"
                    />
                    <span>Financial security fears</span>
                  </li>
                  <li className="flex items-start">
                    <ChevronRight
                      size={16}
                      className="mr-2 mt-1 flex-shrink-0"
                    />
                    <span>Peer success comparison</span>
                  </li>
                  <li className="flex items-start">
                    <ChevronRight
                      size={16}
                      className="mr-2 mt-1 flex-shrink-0"
                    />
                    <span>Family expectation pressure</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-lg shadow-lg">
                <h4 className="font-bold text-lg mb-3 text-purple-900">
                  Middle Class Motivators
                </h4>
                <ul className="space-y-2 text-sm text-purple-800">
                  <li className="flex items-start">
                    <ChevronRight
                      size={16}
                      className="mr-2 mt-1 flex-shrink-0"
                    />
                    <span>Professional status enhancement</span>
                  </li>
                  <li className="flex items-start">
                    <ChevronRight
                      size={16}
                      className="mr-2 mt-1 flex-shrink-0"
                    />
                    <span>Children's future security</span>
                  </li>
                  <li className="flex items-start">
                    <ChevronRight
                      size={16}
                      className="mr-2 mt-1 flex-shrink-0"
                    />
                    <span>Financial independence goals</span>
                  </li>
                  <li className="flex items-start">
                    <ChevronRight
                      size={16}
                      className="mr-2 mt-1 flex-shrink-0"
                    />
                    <span>Social mobility aspirations</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-pink-50 to-pink-100 p-6 rounded-lg shadow-lg">
                <h4 className="font-bold text-lg mb-3 text-pink-900">
                  Upper Middle Motivators
                </h4>
                <ul className="space-y-2 text-sm text-pink-800">
                  <li className="flex items-start">
                    <ChevronRight
                      size={16}
                      className="mr-2 mt-1 flex-shrink-0"
                    />
                    <span>Business growth acceleration</span>
                  </li>
                  <li className="flex items-start">
                    <ChevronRight
                      size={16}
                      className="mr-2 mt-1 flex-shrink-0"
                    />
                    <span>Industry leadership positioning</span>
                  </li>
                  <li className="flex items-start">
                    <ChevronRight
                      size={16}
                      className="mr-2 mt-1 flex-shrink-0"
                    />
                    <span>Legacy building for family</span>
                  </li>
                  <li className="flex items-start">
                    <ChevronRight
                      size={16}
                      className="mr-2 mt-1 flex-shrink-0"
                    />
                    <span>Network and influence expansion</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Cultural Tab */}
        {activeTab === "cultural" && (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h3 className="text-xl font-bold text-gray-800 mb-4">
                Cultural Value Impact on Purchase Decisions
              </h3>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={culturalValues} layout="horizontal">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" domain={[0, 100]} />
                  <YAxis dataKey="value" type="category" width={150} />
                  <Tooltip />
                  <Bar dataKey="impact" fill="#8b5cf6">
                    {culturalValues.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-lg">
                <h4 className="font-bold text-lg mb-4">Core Cultural Values</h4>
                <div className="space-y-3">
                  <div className="border-l-4 border-blue-500 pl-4">
                    <div className="font-semibold text-gray-800">
                      Ubuntu Philosophy
                    </div>
                    <div className="text-sm text-gray-600">
                      Community-centric decision making
                    </div>
                  </div>
                  <div className="border-l-4 border-purple-500 pl-4">
                    <div className="font-semibold text-gray-800">
                      Family First
                    </div>
                    <div className="text-sm text-gray-600">
                      Extended family responsibility matters
                    </div>
                  </div>
                  <div className="border-l-4 border-pink-500 pl-4">
                    <div className="font-semibold text-gray-800">
                      Faith-Based
                    </div>
                    <div className="text-sm text-gray-600">
                      Religious consultation in major decisions
                    </div>
                  </div>
                  <div className="border-l-4 border-orange-500 pl-4">
                    <div className="font-semibold text-gray-800">
                      Status Conscious
                    </div>
                    <div className="text-sm text-gray-600">
                      Education and titles highly valued
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-lg">
                <h4 className="font-bold text-lg mb-4">
                  Trust Building Essentials
                </h4>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-blue-50 rounded">
                    <span className="font-medium">Testimonials</span>
                    <span className="text-2xl font-bold text-blue-600">
                      92%
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-purple-50 rounded">
                    <span className="font-medium">
                      Professional Credentials
                    </span>
                    <span className="text-2xl font-bold text-purple-600">
                      88%
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-pink-50 rounded">
                    <span className="font-medium">Referrals</span>
                    <span className="text-2xl font-bold text-pink-600">
                      90%
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-orange-50 rounded">
                    <span className="font-medium">Social Proof</span>
                    <span className="text-2xl font-bold text-orange-600">
                      85%
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 rounded-lg shadow-lg text-white">
              <h4 className="font-bold text-lg mb-3">
                Regional Considerations
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <div className="font-semibold mb-2">Northern Nigeria</div>
                  <div className="opacity-90">
                    Islamic influences, traditional authority, conservative
                    spending
                  </div>
                </div>
                <div>
                  <div className="font-semibold mb-2">Middle Belt</div>
                  <div className="opacity-90">
                    Religious diversity, agricultural mindset, community-based
                    decisions
                  </div>
                </div>
                <div>
                  <div className="font-semibold mb-2">Southern Nigeria</div>
                  <div className="opacity-90">
                    Christian influences, entrepreneurial culture, urban
                    lifestyle
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Opportunity Tab */}
        {activeTab === "opportunity" && (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h3 className="text-xl font-bold text-gray-800 mb-4">
                Market Opportunity Assessment Matrix
              </h3>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={opportunityData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="segment"
                    angle={-15}
                    textAnchor="end"
                    height={70}
                  />
                  <YAxis domain={[0, 10]} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="market" fill="#3b82f6" name="Market Size" />
                  <Bar
                    dataKey="competition"
                    fill="#ef4444"
                    name="Competition"
                  />
                  <Bar dataKey="margin" fill="#10b981" name="Profit Margin" />
                  <Bar
                    dataKey="score"
                    fill="#8b5cf6"
                    name="Opportunity Score"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white p-6 rounded-lg shadow-lg border-t-4 border-green-500">
                <div className="text-center mb-4">
                  <div className="text-3xl font-bold text-green-600">8/10</div>
                  <div className="text-gray-600 font-medium">
                    Best Opportunity
                  </div>
                </div>
                <h4 className="font-bold text-lg mb-2">Middle Class Segment</h4>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li>✓ Largest buying population</li>
                  <li>✓ Moderate competition</li>
                  <li>✓ Good profit margins</li>
                  <li>✓ Quality-conscious buyers</li>
                  <li>✓ Willing to invest in proven solutions</li>
                </ul>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-lg border-t-4 border-blue-500">
                <div className="text-center mb-4">
                  <div className="text-3xl font-bold text-blue-600">7/10</div>
                  <div className="text-gray-600 font-medium">Premium Play</div>
                </div>
                <h4 className="font-bold text-lg mb-2">Upper Middle Segment</h4>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li>✓ High profit margins</li>
                  <li>✓ Low competition</li>
                  <li>✓ Quick decision makers</li>
                  <li>⚠ Smaller market size</li>
                  <li>⚠ High entry difficulty</li>
                </ul>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-lg border-t-4 border-orange-500">
                <div className="text-center mb-4">
                  <div className="text-3xl font-bold text-orange-600">6/10</div>
                  <div className="text-gray-600 font-medium">
                    Volume Strategy
                  </div>
                </div>
                <h4 className="font-bold text-lg mb-2">Entry Level Segment</h4>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li>✓ Largest population</li>
                  <li>✓ Easy entry</li>
                  <li>⚠ High competition</li>
                  <li>⚠ Low profit margins</li>
                  <li>⚠ Price sensitive</li>
                </ul>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h4 className="font-bold text-lg mb-4">
                Strategic Recommendations
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border-l-4 border-green-500 pl-4">
                  <div className="font-semibold text-green-700 mb-2">
                    Start Here
                  </div>
                  <div className="text-sm text-gray-700">
                    Target <strong>Middle Class</strong> segment first. They
                    offer the best balance of market size, purchasing power, and
                    competition. Build credibility here before expanding.
                  </div>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <div className="font-semibold text-blue-700 mb-2">
                    Scale Strategy
                  </div>
                  <div className="text-sm text-gray-700">
                    Once established, create premium offerings for{" "}
                    <strong>Upper Middle</strong> segment. Higher margins fund
                    growth and brand elevation.
                  </div>
                </div>
                <div className="border-l-4 border-orange-500 pl-4">
                  <div className="font-semibold text-orange-700 mb-2">
                    Volume Play
                  </div>
                  <div className="text-sm text-gray-700">
                    Develop low-cost, high-volume products for{" "}
                    <strong>Entry Level</strong> as lead generators and brand
                    awareness tools.
                  </div>
                </div>
                <div className="border-l-4 border-purple-500 pl-4">
                  <div className="font-semibold text-purple-700 mb-2">
                    Pricing Ladder
                  </div>
                  <div className="text-sm text-gray-700">
                    Create tiered offerings: Entry (₦15-35K), Middle (₦75-150K),
                    Premium (₦300-750K) to capture all segments.
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Promotional Section */}
        <div className="mt-8 bg-gradient-to-r from-green-600 to-green-700 rounded-2xl shadow-xl p-8 text-center text-white">
          <h2 className="text-3xl font-bold mb-3">
            Want to Make Money Selling Your Knowledge in 2026?
          </h2>
          <p className="text-lg text-green-50 mb-6 max-w-3xl mx-auto">
            This is how busy salary earners are making at least ₦1M naira extra
            income without quitting their jobs.
          </p>
          <Link
            to="/dbc/free-training"
            className="inline-block bg-white text-green-700 font-semibold px-8 py-4 rounded-lg hover:bg-green-50 transition-colors shadow-lg text-lg"
          >
            Click Here
          </Link>
        </div>

        {/* Footer Insights */}
        <div className="bg-gradient-to-r from-gray-800 to-gray-900 text-white p-6 rounded-lg shadow-lg mt-6">
          <h4 className="font-bold text-lg mb-3">Key Takeaways</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <div className="font-semibold mb-1">
                💡 Cultural Alignment is Critical
              </div>
              <div className="opacity-90">
                Family values, education respect, and community trust are
                non-negotiable for success
              </div>
            </div>
            <div>
              <div className="font-semibold mb-1">
                💰 Middle Class = Sweet Spot
              </div>
              <div className="opacity-90">
                Best balance of market size, purchasing power, and profit
                potential
              </div>
            </div>
            <div>
              <div className="font-semibold mb-1">
                🎯 Segment-Specific Messaging
              </div>
              <div className="opacity-90">
                Different income levels need completely different value
                propositions and proof points
              </div>
            </div>
            <div>
              <div className="font-semibold mb-1">🔐 Trust Takes Time</div>
              <div className="opacity-90">
                Testimonials, credentials, and referrals are essential - invest
                heavily in social proof
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NigerianMarketAnalysisDashboard;
